// src/shared/components/VoiceRecorder.tsx
// ────────────────────────────────────────────────────────────────────────────
// DRY reusable voice recorder — PAC Glass design system.
//
// Renders a microphone button with recording animation, elapsed timer,
// auto-transcription on stop, and transcript display.
//
// Consumers wire it with a single onTranscript callback.
// All recording logic lives in useVoiceCapture — this is a pure renderer.
//
// Usage:
//   <VoiceRecorder onTranscript={(text) => setDescription(d => d + ' ' + text)} />
// ────────────────────────────────────────────────────────────────────────────
import React from 'react';
import {
  useVoiceCapture,
  type VoiceCaptureConfig,
  type VoiceCaptureResult,
} from '@/shared/hooks/useVoiceCapture';

interface VoiceRecorderProps {
  /** Called with transcribed text on successful recording */
  onTranscript: (text: string, result: VoiceCaptureResult) => void;
  /** Optional config overrides for language, model, etc. */
  config?: Partial<VoiceCaptureConfig>;
  /** Compact mode — just the mic button, no transcript display */
  compact?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Additional CSS classes for the wrapper */
  className?: string;
}

function formatElapsed(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function VoiceRecorder({
  onTranscript,
  config,
  compact = false,
  disabled = false,
  className = '',
}: VoiceRecorderProps) {
  const voice = useVoiceCapture(config);

  // Auto-emit onTranscript when done
  const lastTranscriptRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      voice.phase === 'done' &&
      voice.result &&
      voice.result.transcript !== lastTranscriptRef.current
    ) {
      lastTranscriptRef.current = voice.result.transcript;
      onTranscript(voice.result.transcript, voice.result);
    }
  }, [voice.phase, voice.result, onTranscript]);

  // Reset tracked transcript when going back to idle
  React.useEffect(() => {
    if (voice.phase === 'idle') {
      lastTranscriptRef.current = null;
    }
  }, [voice.phase]);

  // ── Mic button ────────────────────────────────────────────────────────
  const micButton = (
    <button
      type="button"
      onClick={voice.toggle}
      disabled={disabled || voice.isProcessing}
      title={
        voice.isRecording
          ? 'Stop recording'
          : voice.isProcessing
            ? 'Processing…'
            : 'Start voice recording'
      }
      className={`
        relative flex items-center justify-center
        w-10 h-10 rounded-full border transition-all duration-200
        ${voice.isRecording
          ? 'border-rose-500/60 bg-rose-500/20 text-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.3)]'
          : voice.isProcessing
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-400 cursor-wait'
            : 'border-white/10 bg-white/[0.04] text-neutral-400 hover:border-purple-500/50 hover:text-purple-400'
        }
        disabled:opacity-30 disabled:cursor-not-allowed
      `}
    >
      {/* Pulsing ring while recording */}
      {voice.isRecording && (
        <span className="absolute inset-0 rounded-full border-2 border-rose-500/40 animate-ping" />
      )}

      {/* Processing spinner */}
      {voice.isProcessing ? (
        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
          <circle
            cx="12" cy="12" r="10"
            stroke="currentColor" strokeWidth="2" opacity="0.3"
          />
          <path
            d="M12 2a10 10 0 0 1 10 10"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          />
        </svg>
      ) : voice.isRecording ? (
        /* Stop icon */
        <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
          <rect x="3" y="3" width="10" height="10" rx="1" />
        </svg>
      ) : (
        /* Microphone icon */
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="1" width="6" height="12" rx="3" />
          <path d="M5 10a7 7 0 0 0 14 0" />
          <line x1="12" y1="17" x2="12" y2="21" />
          <line x1="8" y1="21" x2="16" y2="21" />
        </svg>
      )}
    </button>
  );

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-2 ${className}`}>
        {micButton}
        {voice.isRecording && (
          <span className="text-[10px] text-rose-400 tabular-nums font-mono">
            {formatElapsed(voice.elapsedSec)}
          </span>
        )}
        {voice.isProcessing && (
          <span className="text-[10px] text-amber-400 animate-pulse">Transcribing…</span>
        )}
        {voice.error && (
          <span className="text-[10px] text-rose-400 max-w-[180px] truncate">{voice.error}</span>
        )}
      </div>
    );
  }

  // ── Full mode ─────────────────────────────────────────────────────────
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-3">
        {micButton}

        <div className="flex flex-col">
          {voice.phase === 'idle' && (
            <span className="text-[10px] text-neutral-500">
              Press to record — Spanish auto-detect, English fallback
            </span>
          )}
          {voice.isRecording && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-rose-400 font-mono tabular-nums">
                {formatElapsed(voice.elapsedSec)}
              </span>
              <span className="text-[10px] text-neutral-500">Recording…</span>
              {/* Level bars animation */}
              <div className="flex items-end gap-[2px] h-3">
                {[1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className="w-[3px] bg-rose-400/70 rounded-full animate-pulse"
                    style={{
                      height: `${4 + Math.random() * 8}px`,
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          {voice.isProcessing && (
            <span className="text-xs text-amber-400 animate-pulse">
              Transcribing audio…
            </span>
          )}
          {voice.phase === 'done' && voice.result && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-emerald-400">
                ✓ {voice.result.wordCount} words
              </span>
              {voice.result.detectedLanguage && (
                <span className="text-[10px] text-neutral-500">
                  ({voice.result.detectedLanguage})
                </span>
              )}
              <span className="text-[10px] text-neutral-600">
                {(voice.result.confidence * 100).toFixed(0)}% confidence
              </span>
              <button
                type="button"
                onClick={voice.reset}
                className="text-[10px] text-neutral-500 hover:text-neutral-300 underline"
              >
                Clear
              </button>
            </div>
          )}
          {voice.error && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-rose-400">{voice.error}</span>
              <button
                type="button"
                onClick={voice.reset}
                className="text-[10px] text-neutral-500 hover:text-neutral-300 underline"
              >
                Retry
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Transcript preview (full mode only) */}
      {voice.phase === 'done' && voice.result && (
        <div className="pac-glass-subtle rounded px-3 py-2 border border-emerald-900/20">
          <p className="text-xs text-neutral-300 leading-relaxed">
            {voice.result.transcript}
          </p>
        </div>
      )}
    </div>
  );
}
